import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';

export interface SelectOption {
  value: string;
  label: string;
  hint?: string;
  // Optional leading glyph — currency symbol, country flag, etc.
  prefix?: ReactNode;
  // Optional searchable terms beyond label (e.g. "United States" should
  // also match "USA" and "US").
  searchAliases?: string[];
  disabled?: boolean;
}

export interface SelectProps {
  value: string;
  onChange: (next: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  // Search affordance threshold — show the search input when the list
  // has more than `searchableAt` rows. Default 8.
  searchableAt?: number;
  // Trigger width. 'auto' lets the label sit at its natural width; the
  // popover uses Radix anchor so it always lines up with the trigger.
  width?: 'auto' | 'full';
  // Override trigger className when callers need a different chrome.
  className?: string;
  // ARIA / form label affordance.
  ariaLabel?: string;
}

export function Select({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  searchableAt = 8,
  width = 'full',
  className,
  ariaLabel,
}: SelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value),
    [options, value],
  );

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => {
      const hay = [o.value, o.label, o.hint, ...(o.searchAliases ?? [])]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
    else setQuery('');
  }, [open]);

  const showSearch = options.length > searchableAt;

  return (
    <Popover.Root open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            'inline-flex items-center justify-between gap-2 rounded-md border border-ink-300 bg-white px-3 py-1.5 text-sm text-ink-900',
            'hover:border-ink-400 focus:outline-none focus:ring-2 focus:ring-accent-500/30 focus-visible:border-accent-500',
            'disabled:cursor-not-allowed disabled:opacity-60',
            'data-[state=open]:border-accent-500 data-[state=open]:ring-2 data-[state=open]:ring-accent-500/30',
            width === 'full' && 'w-full',
            className,
          )}
        >
          <span className="flex min-w-0 items-center gap-2 truncate">
            {selected?.prefix && (
              <span className="shrink-0 inline-flex w-5 items-center justify-center text-base leading-none">
                {selected.prefix}
              </span>
            )}
            {selected ? (
              <span className="truncate">{selected.label}</span>
            ) : (
              <span className="truncate text-ink-500">
                {placeholder ?? t('common.select_placeholder')}
              </span>
            )}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-ink-500 shrink-0" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className={cn(
            'z-50 rounded-lg border border-ink-200 bg-white shadow-lg',
            'data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95',
            'w-[var(--radix-popover-trigger-width)] min-w-[14rem]',
          )}
        >
          {showSearch && (
            <div className="flex items-center gap-2 border-b border-ink-200 px-3 py-2">
              <Search className="h-3.5 w-3.5 text-ink-400 shrink-0" aria-hidden />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('common.search')}
                className="w-full bg-transparent text-sm text-ink-900 focus:outline-none placeholder:text-ink-400"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          )}
          <ul className="max-h-72 overflow-y-auto py-1" role="listbox">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-xs text-ink-500">
                {t('common.no_results')}
              </li>
            )}
            {filtered.map((opt) => {
              const active = opt.value === value;
              return (
                <li key={opt.value} role="option" aria-selected={active}>
                  <button
                    type="button"
                    disabled={opt.disabled}
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm',
                      'hover:bg-ink-100 focus:outline-none focus:bg-ink-100',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      active && 'bg-accent-50 text-accent-700',
                    )}
                  >
                    {opt.prefix && (
                      <span className="shrink-0 inline-flex w-5 items-center justify-center text-base leading-none">
                        {opt.prefix}
                      </span>
                    )}
                    <span className="flex-1 min-w-0">
                      <span className="block truncate">{opt.label}</span>
                      {opt.hint && (
                        <span className="block truncate text-[11px] text-ink-500 num">
                          {opt.hint}
                        </span>
                      )}
                    </span>
                    {active && (
                      <Check className="h-3.5 w-3.5 text-accent-600 shrink-0" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
